import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Trophy, ChevronRight, ArrowLeft, Save, Trash2, Loader2,
  Plus, X, AlertTriangle, Info, Users, Briefcase,
  CheckCircle2, XCircle, TrendingUp,
  Tag,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const ROLE_TYPES = [
  { value: 'auditor',    label: 'Auditor',    icon: Users },
  { value: 'leadership', label: 'Leadership', icon: Briefcase },
];

const OVERRIDE_EFFECTS = [
  { value: 'zero_incentive',  label: 'Zero Incentive — no payout for the period' },
  { value: 'disqualify',      label: 'Disqualify — remove from the run entirely' },
  { value: 'reduce_by_pct',   label: 'Reduce by % — reduce payout by a given percentage' },
];

const SCORING_TYPES = [
  { value: 'manual',      label: 'Manual 0–100',    desc: 'User enters a 0-100 score directly' },
  { value: 'banded',      label: 'Tiered bands',    desc: 'Highest matching revenue/threshold band wins' },
  { value: 'categorical', label: 'Category select', desc: 'Score is looked up from a set of named options' },
  { value: 'factor',      label: 'Factor input',    desc: 'Decimal factor (e.g. 0.8 = 80%)' },
];

const MONTH_NAMES = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
];

const ASSESSMENT_FREQUENCIES = [
  { value: 'quarterly',  label: 'Quarterly' },
  { value: 'monthly',    label: 'Monthly' },
  { value: 'annually',   label: 'Annually' },
];

const COMPONENT_TYPES = [
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Periodic (Quarterly)' },
];

// ─── Band editor ─────────────────────────────────────────────────────────────
function BandEditor({ bands, onChange, readOnly, currencyKey }) {
  const isCurrency = currencyKey === 'revenue';

  const update = (idx, field, val) => {
    const next = [...bands];
    next[idx] = { ...next[idx], [field]: val };
    onChange(next);
  };
  const add = () => onChange([...bands, { threshold: 0, score_pct: 0 }]);
  const remove = (idx) => onChange(bands.filter((_, i) => i !== idx));

  return (
    <div className="mt-2 space-y-1.5" data-testid="band-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <TrendingUp className="h-3 w-3" />
          Threshold bands — highest matching band wins
        </span>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={add}
            className="h-6 text-xs px-2 gap-1" data-testid="add-band-btn">
            <Plus className="h-3 w-3" /> Add Band
          </Button>
        )}
      </div>
      {bands.length === 0 && (
        <p className="text-xs text-amber-600 italic">No bands — add at least one band.</p>
      )}
      <div className="space-y-1">
        {bands.map((b, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-1.5 items-center"
            data-testid={`band-row-${idx}`}>
            <div className="col-span-1 text-xs text-slate-400 text-right">≥</div>
            <div className="col-span-5">
              <div className="flex items-center gap-1">
                {isCurrency && <span className="text-xs text-slate-400">£</span>}
                <Input
                  type="number" min="0" step={isCurrency ? 1000 : 1}
                  value={b.threshold ?? ''}
                  onChange={e => update(idx, 'threshold', parseFloat(e.target.value) || 0)}
                  className="h-6 text-xs px-1.5"
                  placeholder={isCurrency ? 'e.g. 10000' : 'e.g. 90'}
                  disabled={readOnly}
                  data-testid={`band-threshold-${idx}`}
                />
              </div>
            </div>
            <div className="col-span-1 text-xs text-slate-400 text-center">→</div>
            <div className="col-span-4">
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" step={1}
                  value={b.score_pct ?? ''}
                  onChange={e => update(idx, 'score_pct', parseFloat(e.target.value) || 0)}
                  className="h-6 text-xs px-1.5"
                  placeholder="score %"
                  disabled={readOnly}
                  data-testid={`band-score-${idx}`}
                />
                <span className="text-xs text-slate-400 flex-shrink-0">%</span>
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              {!readOnly && (
                <button onClick={() => remove(idx)}
                  className="text-red-400 hover:text-red-600"
                  data-testid={`remove-band-${idx}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {bands.length > 0 && (
        <p className="text-xs text-slate-400 italic">
          Bands are sorted by threshold automatically. Input with value below the lowest threshold scores 0%.
        </p>
      )}
    </div>
  );
}

// ─── Category editor ──────────────────────────────────────────────────────────
function CategoryEditor({ categories, onChange, readOnly }) {
  const update = (idx, field, val) => {
    const next = [...categories];
    next[idx] = { ...next[idx], [field]: val };
    onChange(next);
  };
  const add = () => onChange([...categories, { value: '', label: '', score_pct: 0 }]);
  const remove = (idx) => onChange(categories.filter((_, i) => i !== idx));

  return (
    <div className="mt-2 space-y-1.5" data-testid="category-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Tag className="h-3 w-3" />
          Named categories — each maps to a score
        </span>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={add}
            className="h-6 text-xs px-2 gap-1" data-testid="add-category-btn">
            <Plus className="h-3 w-3" /> Add Category
          </Button>
        )}
      </div>
      {categories.length === 0 && (
        <p className="text-xs text-amber-600 italic">No categories — add at least one.</p>
      )}
      <div className="space-y-1">
        {/* Header */}
        {categories.length > 0 && (
          <div className="grid grid-cols-12 gap-1.5">
            <div className="col-span-3 text-xs text-slate-400 font-medium">Value (key)</div>
            <div className="col-span-5 text-xs text-slate-400 font-medium">Display label</div>
            <div className="col-span-3 text-xs text-slate-400 font-medium">Score %</div>
          </div>
        )}
        {categories.map((cat, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-1.5 items-center"
            data-testid={`category-row-${idx}`}>
            <div className="col-span-3">
              <Input
                value={cat.value || ''}
                onChange={e => update(idx, 'value', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="e.g. positive"
                className="h-6 text-xs px-1.5"
                disabled={readOnly}
                data-testid={`cat-value-${idx}`}
              />
            </div>
            <div className="col-span-5">
              <Input
                value={cat.label || ''}
                onChange={e => update(idx, 'label', e.target.value)}
                placeholder="e.g. Positive Feedback"
                className="h-6 text-xs px-1.5"
                disabled={readOnly}
                data-testid={`cat-label-${idx}`}
              />
            </div>
            <div className="col-span-3">
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" step={1}
                  value={cat.score_pct ?? ''}
                  onChange={e => update(idx, 'score_pct', parseFloat(e.target.value) || 0)}
                  className="h-6 text-xs px-1.5"
                  placeholder="0-100"
                  disabled={readOnly}
                  data-testid={`cat-score-${idx}`}
                />
                <span className="text-xs text-slate-400">%</span>
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              {!readOnly && (
                <button onClick={() => remove(idx)}
                  className="text-red-400 hover:text-red-600"
                  data-testid={`remove-category-${idx}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Criteria editor ─────────────────────────────────────────────────────────
function CriteriaEditor({ criteria, onChange, readOnly, roleType }) {
  const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
  const isLeadership = roleType === 'leadership';

  // Separate into monthly/quarterly groups for display
  const monthlyCriteria = criteria.filter(c => (c.component || 'monthly') === 'monthly');
  const quarterlyCriteria = criteria.filter(c => c.component === 'quarterly');
  const monthlyWeight = monthlyCriteria.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
  const quarterlyWeight = quarterlyCriteria.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);

  // Adaptive weight indicator
  let weightState = 'ok';
  let weightMsg = `${totalWeight.toFixed(1)}% allocated`;
  if (totalWeight <= 0) {
    weightState = 'error';
    weightMsg = 'No weight allocated';
  } else if (Math.abs(totalWeight - 100) < 0.01) {
    weightState = 'ok';
    weightMsg = `${totalWeight.toFixed(1)}% ✓`;
  } else if (totalWeight < 100) {
    weightState = 'warn';
    weightMsg = `${totalWeight.toFixed(1)}% — consider reaching 100%`;
  } else {
    // > 100 — allowed, informational
    weightState = 'stretch';
    weightMsg = `${totalWeight.toFixed(1)}% — stretch scheme (total > 100% is permitted)`;
  }

  const weightBadgeClass = {
    ok:      'bg-emerald-100 text-emerald-700',
    warn:    'bg-amber-100 text-amber-700',
    error:   'bg-red-100 text-red-700',
    stretch: 'bg-blue-100 text-blue-700',
  }[weightState];

  const update = (idx, field, val) => {
    const next = [...criteria];
    next[idx] = { ...next[idx], [field]: val };
    onChange(next);
  };

  const updateScoringType = (idx, val) => {
    const next = [...criteria];
    const c = { ...next[idx], scoring_type: val };
    // Initialise sub-config when switching type
    if (val === 'banded' && !c.bands?.length)
      c.bands = [{ threshold: 0, score_pct: 0 }, { threshold: 10000, score_pct: 100 }];
    if (val === 'categorical' && !c.categories?.length)
      c.categories = [
        { value: 'none',     label: 'No / Neutral', score_pct: 60 },
        { value: 'positive', label: 'Positive',     score_pct: 100 },
        { value: 'negative', label: 'Negative',     score_pct: 0 },
      ];
    next[idx] = c;
    onChange(next);
  };

  const add = () => onChange([...criteria, {
    key: '', label: '', weight_pct: 0, scoring_type: 'manual', description: '',
  }]);
  const remove = (idx) => onChange(criteria.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-700">Weighted Criteria</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${weightBadgeClass}`}>
            {weightMsg}
          </span>
          {isLeadership && (
            <span className="text-xs text-slate-400">
              Monthly: {monthlyWeight.toFixed(0)}% | Periodic: {quarterlyWeight.toFixed(0)}%
            </span>
          )}
        </div>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={add}
            className="gap-1.5 h-7 text-xs" data-testid="add-criterion-btn">
            <Plus className="h-3.5 w-3.5" /> Add Criterion
          </Button>
        )}
      </div>

      {weightState === 'stretch' && (
        <div className="flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-md">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          Weights sum to {totalWeight.toFixed(1)}% — achieving all criteria will produce a score above 100%,
          which allows the payout to reach the monthly limit. This is intentional for stretch-target schemes.
        </div>
      )}

      {criteria.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-2">No criteria configured.</p>
      ) : (
        <div className="space-y-2">
          {criteria.map((c, idx) => {
            const scoringType = c.scoring_type || 'manual';
            const component = c.component || 'monthly';
            const isQuarterly = component === 'quarterly';
            return (
              <div key={idx}
                className={`border rounded-md p-3 space-y-2 ${isQuarterly && isLeadership ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200 bg-slate-50'}`}
                data-testid={`criterion-row-${idx}`}>
                {/* Component badge for leadership */}
                {isLeadership && (
                  <div className="flex items-center gap-2">
                    <Select
                      value={component}
                      onValueChange={v => update(idx, 'component', v)}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="h-6 text-xs w-[160px]"
                        data-testid={`criterion-component-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPONENT_TYPES.map(ct => (
                          <SelectItem key={ct.value} value={ct.value} className="text-xs">{ct.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-400">
                      {isQuarterly ? 'Assessed periodically, paid in assessment months' : 'Assessed and paid every period'}
                    </span>
                  </div>
                )}
                {/* Row 1: key / label / weight / remove */}
                <div className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-500">Key</Label>
                    <Input
                      value={c.key}
                      onChange={e => update(idx, 'key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                      placeholder="e.g. revenue"
                      className="h-7 text-xs mt-0.5"
                      disabled={readOnly}
                      data-testid={`criterion-key-${idx}`}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs text-slate-500">Label</Label>
                    <Input
                      value={c.label}
                      onChange={e => update(idx, 'label', e.target.value)}
                      placeholder="e.g. Revenue Achievement"
                      className="h-7 text-xs mt-0.5"
                      disabled={readOnly}
                      data-testid={`criterion-label-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Weight %</Label>
                    <Input
                      type="number" min="0" step="0.5"
                      value={c.weight_pct}
                      onChange={e => update(idx, 'weight_pct', parseFloat(e.target.value) || 0)}
                      className="h-7 text-xs mt-0.5"
                      disabled={readOnly}
                      data-testid={`criterion-weight-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Scoring</Label>
                    <Select
                      value={scoringType}
                      onValueChange={v => updateScoringType(idx, v)}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="h-7 text-xs mt-0.5"
                        data-testid={`criterion-scoring-type-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SCORING_TYPES.map(st => (
                          <SelectItem key={st.value} value={st.value} className="text-xs">
                            {st.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex justify-end pt-5">
                    {!readOnly && (
                      <button onClick={() => remove(idx)}
                        className="text-red-400 hover:text-red-600"
                        data-testid={`remove-criterion-${idx}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Row 2: description */}
                <div>
                  <Label className="text-xs text-slate-500">Description (optional)</Label>
                  <Input
                    value={c.description || ''}
                    onChange={e => update(idx, 'description', e.target.value)}
                    placeholder="Brief description…"
                    className="h-7 text-xs mt-0.5"
                    disabled={readOnly}
                    data-testid={`criterion-desc-${idx}`}
                  />
                </div>

                {/* Row 3: band / category sub-editor */}
                {scoringType === 'banded' && (
                  <BandEditor
                    bands={c.bands || []}
                    onChange={bands => update(idx, 'bands', bands)}
                    readOnly={readOnly}
                    currencyKey={c.key}
                  />
                )}
                {scoringType === 'categorical' && (
                  <CategoryEditor
                    categories={c.categories || []}
                    onChange={cats => update(idx, 'categories', cats)}
                    readOnly={readOnly}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Overrides editor ────────────────────────────────────────────────────────
function OverridesEditor({ overrides, onChange, readOnly }) {
  const update = (idx, field, val) => {
    const next = [...overrides];
    next[idx] = { ...next[idx], [field]: val };
    onChange(next);
  };
  const add = () => onChange([...overrides, {
    key: '', label: '', effect: 'zero_incentive', effect_value: null, description: '',
  }]);
  const remove = (idx) => onChange(overrides.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Hard Override Rules</span>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={add}
            className="gap-1.5 h-7 text-xs" data-testid="add-override-btn">
            <Plus className="h-3.5 w-3.5" /> Add Override
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Override rules bypass weighted criteria — if triggered, they directly determine the outcome regardless of scores.
      </p>
      {overrides.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-2">No override rules configured.</p>
      ) : (
        <div className="space-y-2">
          {overrides.map((o, idx) => (
            <div key={idx}
              className="border border-amber-200 rounded-md p-3 bg-amber-50/50 space-y-2"
              data-testid={`override-row-${idx}`}>
              <div className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-3">
                  <Label className="text-xs text-slate-500">Key</Label>
                  <Input
                    value={o.key}
                    onChange={e => update(idx, 'key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="e.g. negative_feedback"
                    className="h-7 text-xs mt-0.5"
                    disabled={readOnly}
                    data-testid={`override-key-${idx}`}
                  />
                </div>
                <div className="col-span-5">
                  <Label className="text-xs text-slate-500">Trigger Label</Label>
                  <Input
                    value={o.label}
                    onChange={e => update(idx, 'label', e.target.value)}
                    placeholder="e.g. Negative Client Feedback"
                    className="h-7 text-xs mt-0.5"
                    disabled={readOnly}
                    data-testid={`override-label-${idx}`}
                  />
                </div>
                <div className="col-span-4 flex justify-end pt-5">
                  {!readOnly && (
                    <button onClick={() => remove(idx)}
                      className="text-red-400 hover:text-red-600"
                      data-testid={`remove-override-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="col-span-7">
                  <Label className="text-xs text-slate-500">Effect</Label>
                  <Select
                    value={o.effect}
                    onValueChange={v => update(idx, 'effect', v)}
                    disabled={readOnly}
                  >
                    <SelectTrigger className="h-7 text-xs mt-0.5"
                      data-testid={`override-effect-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OVERRIDE_EFFECTS.map(e => (
                        <SelectItem key={e.value} value={e.value} className="text-xs">
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {o.effect === 'reduce_by_pct' && (
                  <div className="col-span-4">
                    <Label className="text-xs text-slate-500">Reduce by %</Label>
                    <Input
                      type="number" min="1" max="100" step="1"
                      value={o.effect_value ?? ''}
                      onChange={e => update(idx, 'effect_value', parseFloat(e.target.value) || null)}
                      placeholder="e.g. 50"
                      className="h-7 text-xs mt-0.5"
                      disabled={readOnly}
                      data-testid={`override-effect-value-${idx}`}
                    />
                  </div>
                )}
                <div className="col-span-11">
                  <Label className="text-xs text-slate-500">Description (optional)</Label>
                  <Input
                    value={o.description || ''}
                    onChange={e => update(idx, 'description', e.target.value)}
                    placeholder="Explain when this override applies…"
                    className="h-7 text-xs mt-0.5"
                    disabled={readOnly}
                    data-testid={`override-desc-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function IncentiveSchemeEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();
  const canEdit = isAdmin || isSeniorScheduler;

  const [scheme, setScheme] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [typesLoading, setTypesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [form, setForm] = useState({
    incentive_type_id: '',
    role_type: '',
    is_active: true,
    period_cutoff_day: '',
    revenue_threshold: '',
    on_time_threshold_pct: '',
    notes: '',
    criteria: [],
    overrides: [],
    leadership_config: null,
    net_profit_gate: { enabled: false, threshold: '', reduction_pct: 50 },   // iter507
  });

  const fetchTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const res = await api.get('/incentives/types');
      setTypes(res.data);
    } catch { }
    finally { setTypesLoading(false); }
  }, []);

  const fetchScheme = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/incentives/schemes/${id}`);
      const d = res.data;
      setScheme(d);
      setForm({
        incentive_type_id: String(d.incentive_type_id),
        role_type: d.role_type,
        is_active: d.is_active,
        period_cutoff_day: d.period_cutoff_day ?? '',
        revenue_threshold: d.revenue_threshold ?? '',
        on_time_threshold_pct: d.on_time_threshold_pct ?? '',
        notes: d.notes || '',
        criteria: d.criteria || [],
        overrides: d.overrides || [],
        leadership_config: d.leadership_config || null,
        net_profit_gate: d.net_profit_gate || { enabled: false, threshold: '', reduction_pct: 50 },
      });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to load scheme.'), variant: 'destructive' });
      navigate('/incentives/schemes');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate, toast]);

  useEffect(() => { fetchTypes(); fetchScheme(); }, [fetchTypes, fetchScheme]);

  // Auto-populate default criteria when role_type is selected on new scheme
  const handleRoleTypeChange = (val) => {
    setForm(prev => {
      const newForm = { ...prev, role_type: val };
      if (isNew && prev.criteria.length === 0) {
        const defaults = {
          auditor: [
            {
              key: 'revenue', label: 'Revenue Achievement', weight_pct: 40,
              scoring_type: 'banded',
              bands: [{ threshold: 0, score_pct: 0 }, { threshold: 10000, score_pct: 100 }],
              description: 'Revenue achieved for the period (configure bands to match your targets)',
            },
            {
              key: 'on_time', label: 'On-Time Completion', weight_pct: 40,
              scoring_type: 'manual',
              description: 'Percentage of audits completed on or before the scheduled date (system-derived)',
            },
            {
              key: 'customer_feedback', label: 'Customer Feedback', weight_pct: 20,
              scoring_type: 'categorical',
              categories: [
                { value: 'none',     label: 'No / Neutral Feedback', score_pct: 60 },
                { value: 'positive', label: 'Positive Feedback',     score_pct: 100 },
                { value: 'negative', label: 'Negative Feedback',     score_pct: 0 },
              ],
              description: 'Customer satisfaction rating for the period',
            },
          ],
          leadership: [
            {
              key: 'net_profit', label: 'Net Profit', weight_pct: 55,
              component: 'monthly',
              scoring_type: 'banded',
              bands: [],
              description: 'Net profit achievement — configure threshold bands below',
            },
            {
              key: 'sales', label: 'Sales', weight_pct: 15,
              component: 'monthly',
              scoring_type: 'banded',
              bands: [],
              description: 'Sales achievement — configure threshold bands below',
            },
            {
              key: 'quarterly_objectives', label: 'Quarterly Objectives Achievement', weight_pct: 30,
              component: 'quarterly',
              scoring_type: 'factor',
              description: 'Factor input for quarterly objective achievement (e.g. 0.8 = 80%)',
            },
          ],
        };
        newForm.criteria = defaults[val] || [];
        if (newForm.overrides.length === 0) {
          newForm.overrides = [{
            key: 'negative_feedback', label: 'Negative Client Feedback',
            effect: 'zero_incentive', effect_value: null,
            description: 'Any formal negative client feedback received during the period results in zero incentive payout',
          }];
        }
      }
      // Set leadership config defaults
      if (val === 'leadership' && !prev.leadership_config) {
        newForm.leadership_config = {
          assessment_frequency: 'quarterly',
          assessment_months: [3, 6, 9, 12],
          quarterly_base_months: 3,
          shared_criteria_keys: ['net_profit', 'sales'],
        };
      } else if (val !== 'leadership') {
        newForm.leadership_config = null;
      }
      return newForm;
    });
  };

  const handleSave = async () => {
    if (!form.incentive_type_id || !form.role_type) {
      toast({ title: 'Validation error', description: 'Incentive type and role type are required.', variant: 'destructive' });
      return;
    }
    // No weight-sum hard block — weights > 100% are intentionally allowed for stretch schemes
    const totalWeight = form.criteria.reduce((s, c) => s + (Number(c.weight_pct) || 0), 0);
    if (form.criteria.length > 0 && totalWeight <= 0) {
      toast({ title: 'Validation error', description: 'At least one criterion must have a weight > 0%.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        incentive_type_id: Number(form.incentive_type_id),
        role_type: form.role_type,
        is_active: form.is_active,
        period_cutoff_day: form.period_cutoff_day !== '' ? Number(form.period_cutoff_day) : null,
        revenue_threshold: form.revenue_threshold !== '' ? Number(form.revenue_threshold) : null,
        on_time_threshold_pct: form.on_time_threshold_pct !== '' ? Number(form.on_time_threshold_pct) : null,
        criteria: form.criteria,
        overrides: form.overrides,
        leadership_config: form.role_type === 'leadership' ? form.leadership_config : null,
        // iter507 — send net_profit_gate for auditor schemes only.
        net_profit_gate: form.role_type === 'auditor' && form.net_profit_gate?.enabled
          ? {
              enabled: true,
              threshold: form.net_profit_gate.threshold !== '' && form.net_profit_gate.threshold != null
                ? Number(form.net_profit_gate.threshold) : null,
              reduction_pct: Number(form.net_profit_gate.reduction_pct) || 50,
            }
          : (form.role_type === 'auditor'
              ? { enabled: false, threshold: null, reduction_pct: Number(form.net_profit_gate?.reduction_pct) || 50 }
              : null),
        notes: form.notes || null,
      };

      if (isNew) {
        const res = await api.post('/incentives/schemes', payload);
        toast({ title: 'Created', description: 'Incentive scheme created.' });
        navigate(`/incentives/schemes/${res.data.id}`, { replace: true });
      } else {
        const res = await api.put(`/incentives/schemes/${id}`, payload);
        setScheme(res.data);
        toast({ title: 'Saved', description: 'Scheme updated.' });
      }
    } catch (err) {
      const msg = getApiError(err, 'Failed to save.');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setShowDeleteDialog(false);
    try {
      await api.delete(`/incentives/schemes/${id}`);
      toast({ title: 'Deleted', description: 'Incentive scheme deleted.' });
      navigate('/incentives/schemes');
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete.'), variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const pageTitle = isNew
    ? 'New Incentive Scheme'
    : `${scheme?.incentive_type_name} — ${scheme?.role_type === 'auditor' ? 'Auditor' : 'Leadership'}`;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="scheme-editor-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/incentives" className="hover:text-slate-700 flex items-center gap-1">
          <Trophy className="h-4 w-4" /> Incentives
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to="/incentives/schemes" className="hover:text-slate-700">Rules</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-700 font-medium truncate">{pageTitle}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/incentives/schemes')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{pageTitle}</h1>
            {!isNew && scheme && (
              <div className="flex items-center gap-2 mt-1">
                {form.is_active
                  ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1 text-xs"><CheckCircle2 className="h-3 w-3" />Active</Badge>
                  : <Badge variant="outline" className="text-slate-500 gap-1 text-xs"><XCircle className="h-3 w-3" />Inactive</Badge>}
              </div>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isNew && isAdmin && (
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setShowDeleteDialog(true)} disabled={deleting} data-testid="delete-scheme-btn">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-scheme-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isNew ? 'Create' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/* Section 1: Basic configuration */}
      <Card data-testid="basic-config-card">
        <CardHeader><CardTitle className="text-base">Basic Configuration</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Incentive Type <span className="text-red-500">*</span></Label>
              <Select value={form.incentive_type_id}
                onValueChange={v => setForm(p => ({ ...p, incentive_type_id: v }))}
                disabled={!canEdit || !isNew || typesLoading}>
                <SelectTrigger data-testid="incentive-type-select">
                  <SelectValue placeholder={typesLoading ? 'Loading…' : 'Select type'} />
                </SelectTrigger>
                <SelectContent>
                  {types.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!isNew && <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="h-3 w-3" />Cannot be changed after creation.</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Role Type <span className="text-red-500">*</span></Label>
              <Select value={form.role_type} onValueChange={handleRoleTypeChange} disabled={!canEdit || !isNew}>
                <SelectTrigger data-testid="role-type-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_TYPES.map(r => {
                    const Icon = r.icon;
                    return (
                      <SelectItem key={r.value} value={r.value}>
                        <span className="flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{r.label}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {!isNew && <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="h-3 w-3" />Cannot be changed after creation.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="cutoff">Period Cut-Off Day
                <span className="ml-1 text-slate-400 font-normal text-xs">(1–28)</span>
              </Label>
              <Input id="cutoff" type="number" min="1" max="28" placeholder="e.g. 25"
                value={form.period_cutoff_day}
                onChange={e => setForm(p => ({ ...p, period_cutoff_day: e.target.value }))}
                disabled={!canEdit} data-testid="period-cutoff-input" />
              <p className="text-xs text-slate-400">Leave blank for no cut-off</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="revthr">Eligibility: Min Revenue (£)</Label>
              <Input id="revthr" type="number" min="0" step="100" placeholder="e.g. 10000"
                value={form.revenue_threshold}
                onChange={e => setForm(p => ({ ...p, revenue_threshold: e.target.value }))}
                disabled={!canEdit} data-testid="revenue-threshold-input" />
              <p className="text-xs text-slate-400">Must be met to qualify (gate only)</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ontime">Eligibility: On-Time (%)</Label>
              <Input id="ontime" type="number" min="0" max="100" step="1" placeholder="e.g. 90"
                value={form.on_time_threshold_pct}
                onChange={e => setForm(p => ({ ...p, on_time_threshold_pct: e.target.value }))}
                disabled={!canEdit} data-testid="on-time-threshold-input" />
              <p className="text-xs text-slate-400">Minimum % required (gate only)</p>
            </div>
          </div>

          {!isNew && (
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={form.is_active}
                onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))}
                disabled={!canEdit} data-testid="is-active-toggle" />
              <Label className="cursor-pointer">{form.is_active ? 'Active' : 'Inactive'}</Label>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
            <Textarea id="notes" rows={2} placeholder="Any notes about this scheme…"
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              disabled={!canEdit} data-testid="notes-textarea" />
          </div>
        </CardContent>
      </Card>

      {/* Section 1b: Leadership Quarterly Configuration */}
      {form.role_type === 'leadership' && (
        <Card data-testid="leadership-config-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Periodic Assessment Configuration
              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">Leadership</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">
              Criteria tagged as "Periodic" accrue monthly but are paid only in assessment months.
              The periodic base = monthly cap × number of accrual months.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Assessment Frequency</Label>
                <Select
                  value={form.leadership_config?.assessment_frequency || 'quarterly'}
                  onValueChange={v => setForm(p => ({
                    ...p,
                    leadership_config: { ...p.leadership_config, assessment_frequency: v },
                  }))}
                  disabled={!canEdit}
                >
                  <SelectTrigger data-testid="assessment-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSESSMENT_FREQUENCIES.map(f => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Accrual Months (base multiplier)</Label>
                <Input
                  type="number" min="1" max="12" step="1"
                  value={form.leadership_config?.quarterly_base_months ?? 3}
                  onChange={e => setForm(p => ({
                    ...p,
                    leadership_config: { ...p.leadership_config, quarterly_base_months: parseInt(e.target.value) || 3 },
                  }))}
                  disabled={!canEdit}
                  data-testid="accrual-months-input"
                />
                <p className="text-xs text-slate-400">Periodic base = monthly cap × this value</p>
              </div>
              <div className="space-y-1.5">
                <Label>Shared Input Keys</Label>
                <Input
                  value={(form.leadership_config?.shared_criteria_keys || []).join(', ')}
                  onChange={e => setForm(p => ({
                    ...p,
                    leadership_config: {
                      ...p.leadership_config,
                      shared_criteria_keys: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                    },
                  }))}
                  placeholder="net_profit, sales"
                  disabled={!canEdit}
                  data-testid="shared-keys-input"
                />
                <p className="text-xs text-slate-400">Criteria entered once for all leaders</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Assessment Months</Label>
              <div className="flex flex-wrap gap-1.5">
                {MONTH_NAMES.map(m => {
                  const selected = (form.leadership_config?.assessment_months || []).includes(m.value);
                  return (
                    <button
                      key={m.value}
                      type="button"
                      className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                      }`}
                      onClick={() => {
                        if (!canEdit) return;
                        const current = form.leadership_config?.assessment_months || [];
                        const next = selected ? current.filter(v => v !== m.value) : [...current, m.value].sort((a, b) => a - b);
                        setForm(p => ({
                          ...p,
                          leadership_config: { ...p.leadership_config, assessment_months: next },
                        }));
                      }}
                      disabled={!canEdit}
                      data-testid={`assessment-month-${m.value}`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400">
                Periodic criteria are paid only when a run's period ends in one of these months.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 2: Weighted criteria */}
      <Card data-testid="criteria-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            Scoring Criteria
            <span className="text-xs font-normal text-slate-400">
              Weights ≥ 100% are permitted for stretch-target schemes
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CriteriaEditor
            criteria={form.criteria}
            onChange={val => setForm(p => ({ ...p, criteria: val }))}
            readOnly={!canEdit}
            roleType={form.role_type}
          />
        </CardContent>
      </Card>

      {/* Section 3: Override rules */}
      <Card data-testid="overrides-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Hard Override Rules
            <Badge variant="outline" className="text-xs">Bypass scoring</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <OverridesEditor
            overrides={form.overrides}
            onChange={val => setForm(p => ({ ...p, overrides: val }))}
            readOnly={!canEdit}
          />
        </CardContent>
      </Card>

      {/* iter507 — Section 4: Net Profit Gate (auditor only) */}
      {form.role_type === 'auditor' && (
        <Card data-testid="net-profit-gate-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Net Profit Gate
              <Badge variant="outline" className="text-xs">Applied last</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">
              If the month's Net Profit (entered in the Senior Leadership
              Monthly Scheme <code className="bg-slate-100 px-1 rounded">net_profit</code> input)
              is below the threshold, every auditor beneficiary's final
              payout is reduced by the configured percentage. This is applied
              AFTER all other calculations (criteria, overrides, monthly cap).
              If no Leadership run exists for the month, Net Profit is treated
              as £0 so the gate fires.
            </p>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.net_profit_gate?.enabled}
                onChange={e => setForm(p => ({
                  ...p,
                  net_profit_gate: {
                    ...(p.net_profit_gate || {}),
                    enabled: e.target.checked,
                    reduction_pct: p.net_profit_gate?.reduction_pct ?? 50,
                  },
                }))}
                disabled={!canEdit}
                data-testid="net-profit-gate-enabled"
              />
              <span className="font-medium text-slate-700">Enable Net Profit gate for this scheme</span>
            </label>

            {form.net_profit_gate?.enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="np-threshold">Net Profit Threshold (£)</Label>
                  <Input
                    id="np-threshold"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.net_profit_gate?.threshold ?? ''}
                    onChange={e => setForm(p => ({
                      ...p,
                      net_profit_gate: { ...(p.net_profit_gate || {}), threshold: e.target.value },
                    }))}
                    placeholder="e.g. 100000"
                    disabled={!canEdit}
                    data-testid="net-profit-gate-threshold"
                  />
                  <p className="text-xs text-slate-400">Reduction fires when month's Net Profit &lt; this value.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="np-reduction">Reduction (%)</Label>
                  <Input
                    id="np-reduction"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.net_profit_gate?.reduction_pct ?? 50}
                    onChange={e => setForm(p => ({
                      ...p,
                      net_profit_gate: { ...(p.net_profit_gate || {}), reduction_pct: e.target.value },
                    }))}
                    disabled={!canEdit}
                    data-testid="net-profit-gate-reduction"
                  />
                  <p className="text-xs text-slate-400">Default 50%. Editable per scheme (0-100).</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Audit trail */}
      {!isNew && scheme && (
        <Card data-testid="audit-trail-card">
          <CardHeader><CardTitle className="text-base">Audit Trail</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-slate-500">Created by <span className="text-slate-700 font-medium">{scheme.created_by_name || '—'}</span> on {new Date(scheme.created_at).toLocaleString('en-GB')}</p>
            {scheme.updated_at && (
              <p className="text-slate-500">Last updated by <span className="text-slate-700 font-medium">{scheme.updated_by_name || '—'}</span> on {new Date(scheme.updated_at).toLocaleString('en-GB')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Incentive Scheme?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all rules and configuration for this scheme. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
